import React, { useState, useEffect } from 'react';
import '../styles/components/group-standings.scss';
import { baseURL, powerStatsAPI, CURRENT_SEASON } from '../Constants';
import TournamentBracket from './TournamentBracket';
import BracketView from './BracketView';
import { Link } from 'react-router-dom';
import axios from 'axios';

// Define types for our data structure
interface Team {
  teamId: string;
  teamName: string;
  teamLogo: string | null;
  ownerId: string;
  season: number;
  division: string;
  twitter: string | null;
  coachID: string | null;
  website: string | null;
  seed: string;
  playoffSeed: string;
}

interface TeamStanding {
  team: Team;
  gamesPlayed: number;
  wins: number;
  roundDifference: number;
  points: number;
  rank: number;
  rankChange: number;
  headToHeadWins: Record<string, number>;
  headToHeadRoundDifference: Record<string, number>;
}

interface GroupStandings {
  [group: string]: TeamStanding[];
}

interface GroupsLockedResponse {
  season: number;
  groupsVisible: boolean;
  message: string;
  announcementDate: string | null;
  teamsCount: number;
}

interface GroupStandingsProps {
  initialTab?: 'groups' | 'bracket';
  season?: number;
}

// Countdown timer component for announcement date
const CountdownTimer: React.FC<{ announcementDate: string }> = ({ announcementDate }) => {
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const target = new Date(announcementDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        setTimeRemaining('Soon!');
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeRemaining(`${days}d ${hours}h ${minutes}m`);
      } else if (hours > 0) {
        setTimeRemaining(`${hours}h ${minutes}m`);
      } else {
        setTimeRemaining(`${minutes}m`);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [announcementDate]);

  return <span className="countdown-timer">{timeRemaining}</span>;
};

// Team link component for navigation
const TeamLink = ({ team }: { team: Team }) => {
  return <Link to={`/teams/${team.teamId}`}>{team.teamName}</Link>;
};

const GroupStandings: React.FC<GroupStandingsProps> = ({ 
  initialTab = 'groups',
  season: propSeason
}) => {
  const [standings, setStandings] = useState<GroupStandings>({});
  const [season, setSeason] = useState<number>(propSeason || CURRENT_SEASON);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Sync season state when prop changes
  useEffect(() => {
    if (propSeason !== undefined) {
      setSeason(propSeason);
    }
  }, [propSeason]);
  const [viewMode, setViewMode] = useState<'groups' | 'bracket'>(initialTab);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [detailedView, setDetailedView] = useState<boolean>(false);
  const [groupsLocked, setGroupsLocked] = useState<GroupsLockedResponse | null>(null);
  const [season1TeamLogos, setSeason1TeamLogos] = useState<Record<string, string>>({});

  const teamPlaceholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23333'/%3E%3Ctext x='50' y='50' font-size='18' text-anchor='middle' alignment-baseline='middle' font-family='sans-serif' fill='%23fff'%3ENo Logo%3C/text%3E%3C/svg%3E";

  // Helper to convert short IDs to UUID format
  const shortIdToUuid = (shortId: string): string => {
    if (shortId.includes('-')) return shortId;
    const paddedId = shortId.padStart(12, '0');
    return `00000000-0000-0000-0000-${paddedId}`;
  };
  
  // Determine the effective season to use - prefer prop over internal state
  const effectiveSeason = propSeason ?? season;

  // Update internal season state when prop changes (for when component has its own controls)
  useEffect(() => {
    if (propSeason !== undefined && propSeason !== season) {
      setSeason(propSeason);
    }
  }, [propSeason, season]);

  // Fetch season 1 team logos from backend
  useEffect(() => {
    const fetchSeason1Logos = async () => {
      if (effectiveSeason !== 1) return;

      try {
        const response = await axios.get(`${baseURL}/Team?season=1`);
        if (response.data && Array.isArray(response.data)) {
          const logoMap: Record<string, string> = {};
          response.data.forEach((team: any) => {
            if (team.id && team.logo) {
              logoMap[team.id] = team.logo;
            }
          });
          setSeason1TeamLogos(logoMap);
        }
      } catch (error) {
        console.error('Failed to fetch season 1 team logos:', error);
      }
    };

    fetchSeason1Logos();
  }, [effectiveSeason]);

  // Fetch data based on effective season with request cancellation to prevent race conditions
  useEffect(() => {
    if (viewMode !== 'groups') return;

    console.log(`[GroupStandings] Effect triggered - effectiveSeason: ${effectiveSeason}, propSeason: ${propSeason}, internalSeason: ${season}`);

    // Create abort controller for this specific request
    const abortController = new AbortController();
    let isCancelled = false;

    const fetchGroupStandings = async (seasonNum: number) => {
      console.log(`[GroupStandings] fetchGroupStandings called with seasonNum: ${seasonNum}`);
      setIsLoading(true);
      setGroupsLocked(null);

      try {
        // Season 1 & 2: Use PowerStats API (legacy behavior)
        if (seasonNum <= 2) {
          console.log(`[GroupStandings] Season ${seasonNum} <= 2, using PowerStats API only`);
          const response = await fetch(
            `${powerStatsAPI}/Match/getGroupStandings?season=${seasonNum}`,
            { signal: abortController.signal }
          );
          if (!response.ok) {
            throw new Error('Failed to fetch group standings');
          }

          const standingsData = await response.json();
          // Only update state if this request wasn't cancelled
          if (!isCancelled) {
            console.log(`[GroupStandings] Season ${seasonNum} - Setting standings data, groups:`, Object.keys(standingsData));

            // For season 1, filter out duplicate teams with 0 games played
            // (These are duplicates from ID migration: short IDs like "69" vs UUIDs)
            if (seasonNum === 1) {
              const filteredStandings: GroupStandings = {};
              for (const [groupId, teams] of Object.entries(standingsData)) {
                filteredStandings[groupId] = (teams as TeamStanding[]).filter(
                  standing => standing.gamesPlayed > 0
                );
              }
              setStandings(filteredStandings);
            } else {
              setStandings(standingsData);
            }
          } else {
            console.log(`[GroupStandings] Season ${seasonNum} - Request was cancelled, ignoring response`);
          }
        }
        // Season 3+: Use NCS backend API with lock check
        else {
          console.log(`[GroupStandings] Season ${seasonNum} > 2, checking lock status via Team API`);
          const response = await axios.get(`${baseURL}/Team/season/${seasonNum}`, {
            signal: abortController.signal
          });

          // Only update state if this request wasn't cancelled
          if (isCancelled) {
            console.log(`[GroupStandings] Season ${seasonNum} - Request was cancelled after Team API, ignoring`);
            return;
          }

          // Check if response is a locked response
          if (response.data.groupsVisible === false) {
            console.log(`[GroupStandings] Season ${seasonNum} - Groups are LOCKED`);
            setGroupsLocked(response.data);
            setStandings({});
          } else {
            console.log(`[GroupStandings] Season ${seasonNum} - Groups are visible, fetching from PowerStats`);
            // Groups are visible - fetch from PowerStats API
            const standingsResponse = await fetch(
              `${powerStatsAPI}/Match/getGroupStandings?season=${seasonNum}`,
              { signal: abortController.signal }
            );
            if (!standingsResponse.ok) {
              throw new Error('Failed to fetch group standings');
            }

            const standingsData = await standingsResponse.json();
            if (!isCancelled) {
              console.log(`[GroupStandings] Season ${seasonNum} - Setting standings data`);
              setStandings(standingsData);
            } else {
              console.log(`[GroupStandings] Season ${seasonNum} - Request was cancelled, ignoring response`);
            }
          }
        }
      } catch (error: any) {
        // Don't log or update state for aborted requests
        if (error.name === 'AbortError' || error.name === 'CanceledError' || isCancelled) {
          console.log(`[GroupStandings] Season ${seasonNum} - Request aborted/cancelled`);
          return;
        }
        console.error('[GroupStandings] Error fetching group standings:', error);
        setStandings({});
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchGroupStandings(effectiveSeason);

    // Cleanup: cancel request when season changes or component unmounts
    return () => {
      console.log(`[GroupStandings] Cleanup - cancelling request for season ${effectiveSeason}`);
      isCancelled = true;
      abortController.abort();
    };
  }, [effectiveSeason, viewMode]);

  const handleSeasonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSeason(parseInt(event.target.value));
  };

  const handleViewModeChange = (mode: 'groups' | 'bracket') => {
    setViewMode(mode);
    // Reset detailed view when changing view mode
    setDetailedView(false);
    setSelectedGroup(null);
  };

  const toggleDetailedView = (groupId: string) => {
    if (selectedGroup === groupId && detailedView) {
      // If already viewing details for this group, return to overview
      setDetailedView(false);
      setSelectedGroup(null);
    } else {
      // View details for this group
      setDetailedView(true);
      setSelectedGroup(groupId);
    }
  };

  // Helper function to handle team logos that might be null
  const getTeamLogoUrl = (team: Team) => {
    // For season 1, use cached logos from backend
    if (effectiveSeason === 1) {
      // Try direct lookup
      if (season1TeamLogos[team.teamId]) {
        return season1TeamLogos[team.teamId];
      }
      // Try converting short ID to UUID format
      const uuidId = shortIdToUuid(team.teamId);
      if (season1TeamLogos[uuidId]) {
        return season1TeamLogos[uuidId];
      }
      // Fallback to API logo or placeholder
      return team.teamLogo || teamPlaceholderImage;
    }
    return `${baseURL}/Team/${team.teamId}/logo`;
  };


  // Render locked groups message
  const renderLockedMessage = (lockedData: GroupsLockedResponse) => {
    return (
      <div className="groups-locked">
        <div className="locked-content">
          <div className="lock-icon">🔒</div>
          <h2>{lockedData.message}</h2>
          {lockedData.announcementDate ? (
            <div className="announcement-info">
              <p>Groups will be announced in:</p>
              <CountdownTimer announcementDate={lockedData.announcementDate} />
              <p className="announcement-date">
                {new Date(lockedData.announcementDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          ) : (
            <p className="locked-description">
              Groups are currently being finalized. Check back soon!
            </p>
          )}
        </div>
      </div>
    );
  };

  // Render detailed team standings view
  const renderDetailedGroupView = (groupId: string, groupTeams: TeamStanding[]) => {
    // Sort by rank from backend (proper tournament standings)
    // Fall back to seed only if no games played (points === 0)
    const sortedTeams = [...groupTeams].sort((a, b) => {
      // If both have points, use the backend-calculated rank
      if (a.points > 0 || b.points > 0) {
        return a.rank - b.rank;
      }
      // If no games played yet, use initial seeding
      return parseInt(a.team.seed || '999') - parseInt(b.team.seed || '999');
    });
    return (
      <div className="detailed-standings">
        <button 
          className="back-button" 
          onClick={() => toggleDetailedView(groupId)}
        >
          ← Back to Groups Overview
        </button>
        
        <h2 className="group-title">GROUP {groupId === '1' ? 'A' : 'B'} - Detailed Stats</h2>
        
        <div className="detailed-table">
          <div className="table-header">
            <div className="column team">Team</div>
            <div className="column games">Games</div>
            <div className="column wins">W</div>
            <div className="column losses">L</div>
            <div className="column round-diff">Round +/-</div>
            <div className="column points">Points</div>
          </div>
          
          {sortedTeams.map((standing) => {
            const losses = standing.gamesPlayed - standing.wins;
            
            return (
              <div 
                key={standing.team.teamId} 
                className={`table-row ${standing.rank === 1 ? 'first-place' : ''}`}
              >
                <div className="column team">
                  <div className="team-info">
                    <img 
                      src={getTeamLogoUrl(standing.team)} 
                      alt={`${standing.team.teamName} logo`}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = teamPlaceholderImage;
                      }}
                    />
                    <TeamLink team={standing.team} />
                  </div>
                </div>
                <div className="column games">{standing.gamesPlayed}</div>
                <div className="column wins">{standing.wins}</div>
                <div className="column losses">{losses}</div>
                <div className={`column round-diff ${standing.roundDifference > 0 ? 'positive' : standing.roundDifference < 0 ? 'negative' : ''}`}>
                  {standing.roundDifference > 0 ? '+' : ''}{standing.roundDifference}
                </div>
                <div className="column points">{standing.points}</div>
              </div>
            );
          })}
        </div>
        
        {/* Head-to-head results section */}
        <div className="h2h-section">
          <h3>Head-to-Head Results</h3>
          <div className="h2h-grid">
            {sortedTeams.map((standing) => (
              <div key={standing.team.teamId} className="h2h-card">
                <div className="h2h-header">
                  <img 
                    src={getTeamLogoUrl(standing.team)} 
                    alt={`${standing.team.teamName} logo`}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/default-team-logo.png';
                    }}
                  />
                  <h4>{standing.team.teamName}</h4>
                </div>
                <div className="h2h-results">
                  {Object.keys(standing.headToHeadWins).length > 0 ? (
                    <ul>
                      {Object.entries(standing.headToHeadWins).map(([opponentId, wins]) => {
                        const opponent = groupTeams.find(s => s.team.teamId === opponentId)?.team;
                        const roundDiff = standing.headToHeadRoundDifference[opponentId];
                        
                        if (!opponent) return null;
                        
                        return (
                          <li key={opponentId}>
                            <span className="opponent-name">vs {opponent.teamName}:</span>
                            <span className="h2h-stats">
                              <span className={wins > 0 ? 'win' : 'loss'}>
                                {wins}-{1-wins}
                              </span>
                              <span className={`round-diff ${roundDiff > 0 ? 'positive' : roundDiff < 0 ? 'negative' : ''}`}>
                                ({roundDiff > 0 ? '+' : ''}{roundDiff})
                              </span>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="no-matches">No matches played yet</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // When this component is used directly in the StatsPage, show the controls
  // When it's controlled by the MatchesPage, hide the controls
  const showControls = propSeason === undefined;

  return (
    <div className="stats-container">
      <div className="container">
        {showControls && (
          <div className="standings-controls">
            <div className="view-selector">
              <button 
                className={viewMode === 'groups' ? 'active' : ''}
                onClick={() => handleViewModeChange('groups')}
              >
                Groups
              </button>
              <button 
                className={viewMode === 'bracket' ? 'active' : ''}
                onClick={() => handleViewModeChange('bracket')}
              >
                Bracket
              </button>
            </div>
            
            <div className="season-selector">
              <label htmlFor="season-select">Season:</label>
              <select 
                id="season-select" 
                value={season} 
                onChange={handleSeasonChange}
              >
                <option value={1}>Season 1</option>
                <option value={2}>Season 2</option>
              </select>
            </div>
          </div>
        )}

        {viewMode === 'groups' ? (
          <div className="group-standings">
            {isLoading ? (
              <div className="loading">
                <div className="loading-spinner"></div>
                <p>Loading standings...</p>
              </div>
            ) : groupsLocked ? (
              // Show locked message when groups are hidden
              renderLockedMessage(groupsLocked)
            ) : Object.keys(standings).length === 0 ? (
              <div className="no-data">
                <p>No standings data available for the selected season.</p>
              </div>
            ) : (
              <>
                {detailedView && selectedGroup ? (
                  // Detailed view for a specific group
                  renderDetailedGroupView(selectedGroup, standings[selectedGroup])
                ) : (
                  // Overview of all groups
                  <div className="groups-grid">
                    {Object.entries(standings).map(([groupId, groupTeams]) => {
                      // Sort by rank from backend (proper tournament standings)
                      // Fall back to seed only if no games played (points === 0)
                      const sortedTeams = [...groupTeams].sort((a, b) => {
                        if (a.points > 0 || b.points > 0) {
                          return a.rank - b.rank;
                        }
                        return parseInt(a.team.seed || '999') - parseInt(b.team.seed || '999');
                      });
                      return (
                      <div key={groupId} className="group-card">
                        <h2 className="group-title">GROUP {groupId === '1' ? 'A' : 'B'}</h2>
                        
                        <div className="standings-table">
                          <div className="table-header">
                            <div className="column team">Team</div>
                            <div className="column wins">W-L</div>
                            <div className="column round-diff">+/-</div>
                            <div className="column points">PTS</div>
                          </div>
                          
                          {sortedTeams.map((standing) => (
                            <div
                              key={standing.team.teamId}
                              className={`table-row ${standing.rank === 1 ? 'first-place' : ''}`}
                            >
                              <div className="column team">
                                <div className="team-info">
                                  <img
                                    src={getTeamLogoUrl(standing.team)}
                                    alt={`${standing.team.teamName} logo`}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = teamPlaceholderImage;
                                    }}
                                  />
                                  <TeamLink team={standing.team} />
                                </div>
                              </div>
                              <div className="column wins">{standing.wins}-{standing.gamesPlayed - standing.wins}</div>
                              <div className={`column round-diff ${standing.roundDifference > 0 ? 'positive' : standing.roundDifference < 0 ? 'negative' : ''}`}>
                                {standing.roundDifference > 0 ? '+' : ''}{standing.roundDifference}
                              </div>
                              <div className="column points">{standing.points}</div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="group-actions">
                          <button 
                            className="view-details-btn" 
                            onClick={() => toggleDetailedView(groupId)}
                          >
                            View Detailed Stats
                          </button>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          // Use BracketView component directly - now supports all seasons including Season 1
          <div className="bracket-view">
            <BracketView season={season} />
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupStandings;